import HowItWorks from '@components/organisms/HowItWorks/HowItWorks';
import { Container } from '@firsttable/web-components/atoms';
import FeaturedCities from '@firsttable/web-components/organisms/FeaturedCities';
import OurStory from '@firsttable/web-components/organisms/OurStory';
import { space } from '@firsttable/web-theme';
import { useContext } from 'react';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import FeatureFlagBoolean from '@/components/analytics/components/FeatureFlagBoolean';
import {
  PD6630_HOMEPAGE_RESTAURANTS_FLAG,
  PD6630HomepageRestaurants,
  PD6630HowItWorks,
  PD6630HowItWorksHeading,
} from '@/components/analytics/experiments/PD6630HomepageRestaurants';
import { PD7364HomepageRedirect } from '@/components/analytics/experiments/PD7364RedirectLoggedInUsersFromHomepage';
import useFeatureFlagValue from '@/components/analytics/hooks/useFeatureFlagValue';
import { useAuthState } from '../context/AuthContext';
import SilverstripeContext from '../context/Silverstripe';
import GatsbyLinkConvertor from '../GatsbyLinkConvertor';
import ellipse from '../images/ellipse-01.svg?url';
import HomeCities from '../organisms/HomeCities';
import HomePageHero from '../organisms/HomePageHero';
import JoinFirstTable from '../organisms/JoinFirstTable';
import SEO from '../SEO';

const HomePage = () => {
  const {
    page,
    global: { siteConfig, countryStates, regions },
  } = useContext(SilverstripeContext);
  const { isLoggedIn } = useAuthState();
  const isPD6630 = useFeatureFlagValue(PD6630_HOMEPAGE_RESTAURANTS_FLAG, false) === true;
  const safeCountryStates = countryStates ?? [];

  const allRegions = {
    edges: [],
  };

  regions.map((region) => {
    const node = { ...region };
    allRegions.edges.push({ node });
    return region;
  });

  const allCountryStates = {
    edges: [],
  };

  safeCountryStates.map((countryState) => {
    const node = { ...countryState };
    allCountryStates.edges.push({ node });
    return countryState;
  });

  if (!page) {
    return null;
  }

  return (
    <TrackProvider pageEventName="home_viewed" source_page="home">
      <PD7364HomepageRedirect isLoggedIn={isLoggedIn} />
      <SEO
        title={page.metaTitleFormatted}
        id={page.id}
        description={page.metaDescription}
        ogImage={page.ogImage}
        metaTags={page.metaTags}
        slug={page.slug}
      />
      <HomePageHero
        regions={allRegions}
        countryStates={safeCountryStates}
        marginBottom={isPD6630 ? ['xxl', 40] : undefined}
      />
      {isPD6630 && (
        <PD6630HomepageRestaurants
          countryStates={safeCountryStates}
          regions={regions}
          countryCode={siteConfig?.countryCode}
        />
      )}
      <Container mb={[65, 100]}>
        <TrackProvider section="how it works">
          {isPD6630 && <PD6630HowItWorksHeading />}
          {isPD6630 ? (
            <PD6630HowItWorks siteConfig={siteConfig} />
          ) : (
            <HowItWorks siteConfig={siteConfig} />
          )}
        </TrackProvider>
      </Container>
      <FeatureFlagBoolean featureFlag="097:aa_test:landing:web" defaultState="hidden" />
      {!isPD6630 && (
        <>
          <TrackProvider section="featured cities">
            <FeaturedCities states={allCountryStates} NavLinkComponent={GatsbyLinkConvertor} />
          </TrackProvider>
          {!isLoggedIn && (
            <Container
              mb={['50px', '50px', 0]}
              background={[null, null, `url(${ellipse}) no-repeat 0 50%`]}
              pt={[null, null, '175px']}
              pb={[null, null, '175px']}
              pl={[space.s, space.m, '135px']}>
              <TrackProvider section="join first table">
                <JoinFirstTable />
              </TrackProvider>
            </Container>
          )}
          <TrackProvider section="our story">
            <OurStory NavButtonComponent={GatsbyLinkConvertor} />
          </TrackProvider>
        </>
      )}
      <TrackProvider section="home cities">
        <HomeCities data={page.homeCities} />
      </TrackProvider>
    </TrackProvider>
  );
};

export default HomePage;
